import { inject } from '@angular/core';
import { RedirectCommand, ResolveData, ResolveFn, Router } from '@angular/router';

import { firstValueFrom } from 'rxjs';

import { PAGE_NOT_FOUND_QUERY_KEY, PageNotFoundParam } from '@kros-sk/models';
import { DocumentViewTelemetryService, PublicDocumentViewApiService } from '@kros-sk/document-viewer/public-document-view';

import { PUBLIC_DOCUMENT_SUBMODULES } from './public-document-routes';

type ViewDocumentResolvedData = {
  submodule?: string;
  document: unknown;
  /** Internal id from the link response, never the access hash from the URL; what the schema's telemetry reports. */
  documentId: string;
};

type CachedValue = Promise<ViewDocumentResolvedData> | ViewDocumentResolvedData;

export function viewDocumentResponseResolver(): ResolveData {
  const cached: Record<string, CachedValue> = {};

  return {
    submodule: resolver('submodule'),
    document: resolver('document'),
    documentId: resolver('documentId'),
  };

  function resolver(prop: 'submodule'): ResolveFn<RedirectCommand | ViewDocumentResolvedData['submodule']>;
  function resolver(prop: 'document'): ResolveFn<RedirectCommand | ViewDocumentResolvedData['document']>;
  function resolver(prop: 'documentId'): ResolveFn<RedirectCommand | ViewDocumentResolvedData['documentId']>;
  function resolver(prop: keyof ViewDocumentResolvedData): ResolveFn<unknown> {
    return async (route) => {
      const apiService = inject(PublicDocumentViewApiService);
      const telemetry = inject(DocumentViewTelemetryService);
      const router = inject(Router);

      const hash = route.paramMap.get('detailId');

      if (!hash) {
        return redirectToNotFound(router);
      }

      try {
        let cachedValue = cached[hash];

        if (!cachedValue) {
          cachedValue = firstValueFrom(apiService.getDocumentLink(hash)).then(
            (response) => {
              telemetry.setDocumentId(response.detailId);
              telemetry.documentLoaded(String(response.documentType));
              return {
                submodule: PUBLIC_DOCUMENT_SUBMODULES[response.documentType],
                document: response.payload,
                documentId: response.detailId,
              };
            },
            (error: unknown) => {
              telemetry.documentLoadFailed(error);
              throw error;
            }
          );

          cached[hash] = cachedValue;
        }

        const resolved = await cachedValue;
        return resolved[prop];
      } catch {
        return redirectToNotFound(router);
      }
    };
  }
}

function redirectToNotFound(router: Router): RedirectCommand {
  return new RedirectCommand(
    router.createUrlTree(['/not-found'], {
      queryParams: { [PAGE_NOT_FOUND_QUERY_KEY]: PageNotFoundParam.pageNotFound },
    })
  );
}
